<template>
  <div class="ssc">
    <div class="ssc-label">Email sending</div>
    <div v-if="loading" class="text-muted small">Loading...</div>
    <template v-else-if="data">
      <div class="ssc-row">
        <MusubiStatusPill :status="data.state" :tone="tone(data.state)" :label="stateLabel(data)" />
        <span v-if="data.held" class="ssc-held">{{ data.held }} held {{ data.held === 1 ? 'message' : 'messages' }}</span>
      </div>
      <p class="ssc-note">{{ explain(data.state) }}</p>
      <div v-if="data.auto_pause && data.auto_pause.active" class="ssc-auto">
        <strong>Paused automatically on {{ when(data.auto_pause.at) }}</strong> (found by the {{ data.auto_pause.source === 'webhook' ? 'Mailgun webhook' : '15-minute check' }}):
        {{ data.auto_pause.summary }}
        <div class="ssc-auto__nums">{{ numbers(data.auto_pause) }}</div>
        <div>
          Nothing is lost: the held emails wait here until you release or drop them. Setting sending to test or live lifts the pause,
          and the automatic check then counts again from that moment. If this was a false alarm, lift it and release the held emails.
        </div>
      </div>
      <p v-else-if="data.auto_pause" class="ssc-note">
        Last automatic {{ data.auto_pause.mode === 'alert_only' ? 'alert (alert only, not paused)' : 'pause' }} on {{ when(data.auto_pause.at) }}:
        {{ data.auto_pause.summary }}
        <template v-if="data.auto_pause.lifted_at">Lifted on {{ when(data.auto_pause.lifted_at) }} (set to {{ data.auto_pause.lifted_to }}).</template>
      </p>
      <p v-if="platform && platform.autopause && platform.autopause.mode === 'alert_only'" class="ssc-warn">
        The automatic pause is set to alert only (email.autopause=alert_only): high bounce or complaint numbers are reported, but no workspace is paused.
      </p>
      <p v-if="platform && platform.kill_switch" class="ssc-warn">
        The platform kill switch is on ({{ platform.kill_switch }}). Outreach and test mail is held for every workspace.
      </p>
      <p v-if="platform && platform.bypass_active" class="ssc-warn">
        The workspace sending check is bypassed (email.workspace_gate=bypass) while the recipient allowlist is on.
      </p>

      <div class="ssc-row">
        <select v-model="choice" class="form-select form-select-sm ssc-select" :disabled="busy">
          <option v-for="s in states" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>
        <button type="button" class="btn btn-sm btn-primary" :disabled="busy || choice === data.state" @click="setState">
          Set sending
        </button>
      </div>

      <div v-if="data.held" class="ssc-row">
        <button type="button" class="btn btn-sm btn-outline-primary" :disabled="busy" @click="release">Release held messages</button>
        <button type="button" class="btn btn-sm btn-outline-danger" :disabled="busy" @click="drop">Drop held messages</button>
      </div>
      <p v-if="data.held" class="ssc-note">
        Release puts up to 500 held messages back in the queue at a time; each is checked again before it goes.
        Drop means they are never sent.
      </p>

      <div v-if="history.length" class="ssc-history">
        <div class="ssc-sub">Recent changes</div>
        <ul class="ssc-hist-list">
          <li v-for="h in history.slice(0, HISTORY_SHOWN)" :key="h.id">
            <span class="ssc-hist-when">{{ when(h.created) }}</span>
            <span>{{ change(h) }}</span>
            <span class="ssc-hist-who">{{ who(h) }}</span>
          </li>
        </ul>
      </div>

      <div class="ssc-label ssc-label--sub">Cold email</div>
      <div class="ssc-row">
        <select v-model="coldChoice" class="form-select form-select-sm ssc-select" :disabled="busy">
          <option value="registered_only">Registered companies and public sector only</option>
          <option value="any">Any company</option>
        </select>
        <button type="button" class="btn btn-sm btn-primary" :disabled="busy || coldChoice === data.cold_policy" @click="setColdPolicy">
          Set cold email
        </button>
      </div>
      <p class="ssc-note">
        Contacts who signed up with consent are always sent to. For everyone else, registered companies and public sector only
        means campaigns, drips and one-off emails skip contacts whose company is recorded as not registered or has not been checked yet.
      </p>
      <p v-if="platform && platform.cold_policy_off_active" class="ssc-warn">
        The registered-companies check is switched off for every workspace (email.cold_policy_enforce=off) while the recipient allowlist is on.
      </p>

      <template v-if="data.allowance">
        <div class="ssc-label ssc-label--sub">Daily limit</div>
        <div class="ssc-row">
          <input v-model.trim="capInput" type="number" min="0" class="form-control form-control-sm ssc-num" :placeholder="String(data.allowance.workspace.default)" :disabled="busy">
          <button type="button" class="btn btn-sm btn-primary" :disabled="busy" @click="setCap">Set daily limit</button>
          <span class="ssc-held">{{ data.allowance.workspace.used_today }} of {{ data.allowance.workspace.cap }} used today</span>
        </div>
        <p class="ssc-note">
          Campaign, drip and one-off emails a day for this workspace. Leave empty for the platform default ({{ data.allowance.workspace.default }}).
          Emails over the limit wait for the next day.
        </p>
        <p v-if="platform && platform.caps_enforced === false" class="ssc-warn">
          Daily limits are switched off for every workspace (email.caps_enforce=off).
        </p>

        <div v-for="d in data.allowance.domains" :key="d.id" class="ssc-domain">
          <div class="ssc-row">
            <strong class="ssc-domain__name">{{ d.domain }}</strong>
            <span class="ssc-held">
              {{ d.warmup_enabled ? (d.warmup_day ? `warm-up day ${d.warmup_day}, ${d.used_today} of ${d.today_cap} today` : `warm-up not started, ${d.today_cap} on its first day`) : `no warm-up, ${d.used_today} today` }}
            </span>
          </div>
          <div class="ssc-row">
            <label class="ssc-check"><input type="checkbox" v-model="warm[d.id].enabled" :disabled="busy"> Warm-up</label>
            <label class="ssc-mini">start <input v-model.trim="warm[d.id].start" type="number" min="0" class="form-control form-control-sm ssc-num" placeholder="20" :disabled="busy"></label>
            <label class="ssc-mini">add a day <input v-model.trim="warm[d.id].step" type="number" min="0" class="form-control form-control-sm ssc-num" placeholder="15" :disabled="busy"></label>
            <label class="ssc-mini">most a day <input v-model.trim="warm[d.id].ceiling" type="number" min="0" class="form-control form-control-sm ssc-num" :placeholder="String(data.allowance.workspace.cap)" :disabled="busy"></label>
            <button type="button" class="btn btn-sm btn-outline-primary" :disabled="busy" @click="saveWarmup(d.id, false)">Save</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="busy || !d.warmup_started_at" @click="saveWarmup(d.id, true)">Start again from day 1</button>
          </div>
        </div>
        <p v-if="data.allowance.domains.length" class="ssc-note">
          Empty numbers use the defaults: 20 on the first day, adding 15 a day, up to the workspace's daily limit.
          The first day is the day of the domain's first campaign, drip or one-off email.
        </p>
      </template>

      <template v-if="data.inbound && data.inbound.length">
        <div class="ssc-label ssc-label--sub">Replies</div>
        <div v-for="d in data.inbound" :key="'inbound-' + d.id" class="ssc-domain">
          <div class="ssc-row">
            <strong class="ssc-domain__name">{{ d.domain }}</strong>
            <MusubiStatusPill :status="d.ready ? 'ready' : 'not_ready'" :tone="d.ready ? 'success' : 'danger'" :label="d.ready ? 'Takes replies' : 'Not ready for replies'" />
            <span v-if="d.used_by_senders" class="ssc-held">used by this workspace's senders</span>
          </div>
          <p class="ssc-note">Receiving records: {{ receivingText(d) }}. Reply route: {{ d.inbound_state }}{{ d.inbound_route_id ? ' (' + d.inbound_route_id + ')' : '' }}.</p>
          <p v-if="!d.ready" class="ssc-note">Not ready: {{ d.reasons.join('; ') }}.</p>
          <div v-if="routes[d.id]" class="ssc-row">
            <label class="ssc-mini">Mailgun route id
              <input v-model.trim="routes[d.id].route_id" type="text" class="form-control form-control-sm ssc-route" :disabled="busy">
            </label>
            <select v-model="routes[d.id].inbound_state" class="form-select form-select-sm ssc-state" :disabled="busy">
              <option value="">From the route id</option>
              <option value="none">None</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="failed">Failed</option>
            </select>
            <button type="button" class="btn btn-sm btn-outline-primary" :disabled="busy" @click="saveInbound(d.id)">Save route</button>
          </div>
        </div>
        <p class="ssc-note">
          Create each domain's route by hand in Mailgun: match_recipient(".*@" followed by the domain), forward to Yorlin's inbound webhook
          (/api/v1/webhooks/mailgun/inbound), stop. Then record its id here. With the state left on "From the route id", a recorded route is
          active once the domain's receiving records are verified, and pending until then. This workspace can only be set live when every
          domain its senders use takes replies.
        </p>
      </template>
    </template>
    <div v-if="message" class="ssc-msg">{{ message }}</div>
    <div v-if="error" class="alert alert-danger py-1 px-2 small mb-0">{{ error }}</div>
  </div>
</template>

<script>
/**
 * SendingStateControl - the operator's control of one workspace's email
 * sending (send safety slice 2), shown in the tenant drawer of the Usage and
 * plans page. Slice 9 adds the automatic pause: why it tripped and the
 * numbers at that moment. Reads and writes /api/v1/admin/sending/workspaces/{id}
 * (Ops::PLATFORM, the same permission as that page).
 */
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiStatusPill from "@root/components/ui/MusubiStatusPill.vue";
import { sendingStateTone, sendingStateLabel } from "./util/sendingState";

const BASE = '/api/v1/admin/sending/workspaces';
const HISTORY_SHOWN = 5;
const SOURCE_LABELS = {
  operator: 'by Yorlin',
  owner: 'by the owner',
  auto: 'automatically',
  provisioning: 'when the workspace was created',
  migration: 'when the history began',
};

export default {
  name: 'SendingStateControl',
  components: { MusubiStatusPill },
  props: {
    tenantId: { type: Number, required: true },
  },
  // 'state': the workspace's sending state as the server last reported it,
  // { tenantId, state, reason }. Sent after every load and every change, so
  // the page around this control can show the same state without reloading.
  emits: ['state'],
  data() {
    return {
      api: useApi(),
      loading: true,
      busy: false,
      data: null,
      platform: null,
      history: [],
      HISTORY_SHOWN,
      choice: 'off',
      coldChoice: 'registered_only',
      capInput: '',
      warm: {},
      routes: {},
      error: '',
      message: '',
      states: [
        { value: 'off', label: 'Off - nothing is sent' },
        { value: 'test', label: 'Test - own addresses only' },
        { value: 'live', label: 'Live - sending as normal' },
        { value: 'paused', label: 'Paused by Yorlin' },
      ],
    };
  },
  watch: {
    tenantId() { this.load(); },
  },
  mounted() {
    this.load();
  },
  methods: {
    apply(p) {
      if (p && p.data) {
        this.data = p.data;
        this.choice = p.data.state;
        this.coldChoice = p.data.cold_policy || 'registered_only';
        const a = p.data.allowance;
        if (a) {
          this.capInput = a.workspace.own_cap === null || a.workspace.own_cap === undefined ? '' : String(a.workspace.own_cap);
          const warm = {};
          (a.domains || []).forEach((d) => {
            const c = d.custom || {};
            warm[d.id] = {
              enabled: !!d.warmup_enabled,
              start: c.start === null || c.start === undefined ? '' : String(c.start),
              step: c.step === null || c.step === undefined ? '' : String(c.step),
              ceiling: c.ceiling === null || c.ceiling === undefined ? '' : String(c.ceiling),
            };
          });
          this.warm = warm;
        }
        const routes = {};
        (p.data.inbound || []).forEach((d) => {
          routes[d.id] = { route_id: d.inbound_route_id || '', inbound_state: d.inbound_state || '' };
        });
        this.routes = routes;
        this.$emit('state', { tenantId: p.data.id ?? this.tenantId, state: p.data.state, reason: p.data.reason ?? null });
      }
      if (p && p.platform) {
        this.platform = p.platform;
      }
    },
    async load() {
      this.loading = true; this.error = ''; this.message = '';
      try {
        this.apply(await this.api.get(`${BASE}/${this.tenantId}`));
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not load the sending state.';
      }
      this.loading = false;
      this.loadHistory();
    },
    async loadHistory() {
      try {
        const p = await this.api.get(`${BASE}/${this.tenantId}/history`);
        this.history = Array.isArray(p?.data) ? p.data : [];
      } catch (e) {
        this.history = [];
      }
    },
    async setState() {
      this.busy = true; this.error = ''; this.message = '';
      try {
        const p = await this.api.put(`${BASE}/${this.tenantId}`, { state: this.choice });
        if (p?.error) { this.error = p.error; } else { this.apply(p); this.message = 'Sending set to ' + this.choice + '.'; }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not change the sending state.';
      }
      this.busy = false;
      this.loadHistory();
    },
    async setColdPolicy() {
      if (this.coldChoice === 'any' && !window.confirm('Let this workspace send cold email to any company, whatever its registration status?')) return;
      this.busy = true; this.error = ''; this.message = '';
      try {
        const p = await this.api.put(`${BASE}/${this.tenantId}/cold-policy`, { cold_policy: this.coldChoice });
        if (p?.error) { this.error = p.error; } else {
          this.apply(p);
          this.message = this.coldChoice === 'any' ? 'Cold email set to any company.' : 'Cold email set to registered companies and public sector only.';
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not change the cold email setting.';
      }
      this.busy = false;
    },
    async setCap() {
      this.busy = true; this.error = ''; this.message = '';
      try {
        const cap = this.capInput === '' ? null : Number(this.capInput);
        const p = await this.api.put(`${BASE}/${this.tenantId}/daily-cap`, { daily_cap: cap });
        if (p?.error) { this.error = p.error; } else {
          this.apply(p);
          this.message = cap === null ? 'Daily limit set to the platform default.' : `Daily limit set to ${cap}.`;
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not change the daily limit.';
      }
      this.busy = false;
    },
    async saveWarmup(domainId, restart) {
      if (restart && !window.confirm('Start this domain\'s warm-up again from day 1 at its next email?')) return;
      this.busy = true; this.error = ''; this.message = '';
      try {
        const w = this.warm[domainId] || {};
        const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
        const body = restart
          ? { restart: true }
          : { enabled: !!w.enabled, start: num(w.start), step: num(w.step), ceiling: num(w.ceiling) };
        const p = await this.api.put(`${BASE}/${this.tenantId}/domains/${domainId}/warmup`, body);
        if (p?.error) { this.error = p.error; } else {
          this.apply(p);
          this.message = restart ? 'Warm-up will start again from day 1.' : 'Warm-up saved.';
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not change the warm-up.';
      }
      this.busy = false;
    },
    async saveInbound(domainId) {
      this.busy = true; this.error = ''; this.message = '';
      try {
        const r = this.routes[domainId] || {};
        const body = { route_id: r.route_id || null };
        if (r.inbound_state) body.inbound_state = r.inbound_state;
        const p = await this.api.put(`${BASE}/${this.tenantId}/domains/${domainId}/inbound`, body);
        if (p?.error) { this.error = p.error; } else { this.apply(p); this.message = 'Reply route saved.'; }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not save the reply route.';
      }
      this.busy = false;
    },
    receivingText(d) {
      if (!d.receiving) return 'receiving was not switched on when the domain was added';
      if (d.receiving_verified_at) return 'verified ' + this.when(d.receiving_verified_at);
      return d.receiving_checked_at ? 'not found in DNS at the last check, ' + this.when(d.receiving_checked_at) : 'not checked yet';
    },
    async release() {
      this.busy = true; this.error = ''; this.message = '';
      try {
        const p = await this.api.post(`${BASE}/${this.tenantId}/release-held`, {});
        if (p?.error) { this.error = p.error; } else {
          this.apply(p);
          this.message = `Released ${p.released}. ${p.remaining} still held.`;
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not release held messages.';
      }
      this.busy = false;
    },
    async drop() {
      if (!window.confirm('Drop every held message for this workspace? They will never be sent.')) return;
      this.busy = true; this.error = ''; this.message = '';
      try {
        const p = await this.api.post(`${BASE}/${this.tenantId}/drop-held`, {});
        if (p?.error) { this.error = p.error; } else { this.apply(p); this.message = `Dropped ${p.dropped}.`; }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not drop held messages.';
      }
      this.busy = false;
    },
    tone(state) {
      return sendingStateTone(state);
    },
    when(ts) {
      if (!ts) return 'an unknown date';
      return new Date(ts * 1000).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    pct(f) {
      const p = Number(f || 0) * 100;
      return `${Number(p.toFixed(p < 1 ? 2 : 1))}%`;
    },
    numbers(a) {
      const n = a.numbers || {};
      const t = a.thresholds || {};
      return `Since ${this.when(n.since)}: ${n.sent} outreach emails sent, ${n.bounced} hard bounces (${this.pct(n.bounce_rate)}, limit ${this.pct(t.bounce_rate)}), `
        + `${n.complained} spam complaints (${this.pct(n.complaint_rate)}, limit ${this.pct(t.complaint_rate)}), `
        + `at most ${n.complaints_24h} complaints in 24 hours (limit ${t.complaints_day}). The rates count once ${t.min_sent} emails have been sent.`;
    },
    stateLabel(d) {
      return sendingStateLabel(d);
    },
    change(h) {
      const to = h.to_state + (h.to_reason ? ` (${h.to_reason})` : '');
      if (!h.from_state) return `Set to ${to}`;
      const from = h.from_state + (h.from_reason ? ` (${h.from_reason})` : '');
      return from === to ? `Set to ${to} again` : `${from} to ${to}`;
    },
    who(h) {
      const how = SOURCE_LABELS[h.source] || h.source;
      return h.changed_by_name ? `${how}, ${h.changed_by_name}` : how;
    },
    explain(state) {
      return {
        off: 'No campaign, drip, one-off or test email leaves this workspace. Account emails (invites, password resets) still go.',
        test: 'Emails only go to the workspace\'s own addresses: its team and its verified sending domains, and its website domain once a sending domain on it is verified.',
        live: 'Sending as normal.',
        paused: 'Held until sending is set live again. Only Yorlin can lift a pause Yorlin set.',
      }[state] || '';
    },
  },
};
</script>

<style scoped>
.ssc-label { font-size: 0.62rem; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase; color: var(--m-text-tertiary); margin-bottom: 9px; }
.ssc-label--sub { margin-top: 14px; }
.ssc-num { width: 90px; }
.ssc-route { width: 230px; }
.ssc-state { width: 170px; }
.ssc-domain { border-top: 1px solid var(--m-border, #eaecf3); padding-top: 8px; margin-top: 4px; }
.ssc-domain__name { font-size: 0.82rem; }
.ssc-check { display: inline-flex; align-items: center; gap: 5px; font-size: 0.8rem; }
.ssc-mini { display: inline-flex; align-items: center; gap: 5px; font-size: 0.75rem; color: var(--m-text-tertiary); }
.ssc-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
.ssc-select { max-width: 260px; }
.ssc-held { font-size: 0.8rem; color: var(--m-text-secondary); }
.ssc-note { font-size: 0.78rem; color: var(--m-text-tertiary); margin: 0 0 8px; }
.ssc-warn { font-size: 0.78rem; color: var(--m-danger, #b42318); margin: 0 0 8px; }
.ssc-auto { font-size: 0.78rem; color: var(--m-danger, #b42318); border: 1px solid #fecdca; background: #fef3f2; border-radius: 6px; padding: 8px 10px; margin: 0 0 8px; display: flex; flex-direction: column; gap: 4px; }
.ssc-auto__nums { color: var(--m-text-secondary); }
.ssc-msg { font-size: 0.78rem; color: var(--m-text-secondary); margin-bottom: 6px; }
.ssc-history { margin-top: 6px; }
.ssc-sub { font-size: 0.7rem; font-weight: 700; color: var(--m-text-secondary); margin-bottom: 4px; }
.ssc-hist-list { list-style: none; padding: 0; margin: 0 0 8px; font-size: 0.78rem; color: var(--m-text-secondary); }
.ssc-hist-list li { display: flex; gap: 8px; flex-wrap: wrap; padding: 2px 0; }
.ssc-hist-when { color: var(--m-text-tertiary); min-width: 128px; }
.ssc-hist-who { color: var(--m-text-tertiary); }
</style>
